import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Award, CheckCircle, XCircle } from 'lucide-react';
import { getCalendarDB } from '@/lib/supabaseServices';

export default function CalendarHeatmap({ refreshKey }) {
  const [calendarData, setCalendarData] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendar();
  }, [refreshKey]);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const data = await getCalendarDB();
      setCalendarData(data || []);
    } catch (err) {
      console.error('Failed to load calendar data', err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleString('default', { month: 'long' });
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  // Map calendar data by date string YYYY-MM-DD
  const dataMap = {};
  calendarData.forEach((d) => {
    dataMap[d.date] = d;
  });

  const changeMonth = (offset) => {
    const nextDate = new Date(year, month + offset, 1);
    setCurrentDate(nextDate);
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-8 shadow-lg">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-cyan-400" />
            Monthly Performance Calendar & Discipline Heatmap
          </h3>
          <p className="text-xs text-slate-400">
            Daily PnL breakdown, R-multiples, and ICC discipline score tracking
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => changeMonth(-1)}
            className="p-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white border border-slate-700"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-bold text-white min-w-[120px] text-center font-mono">
            {monthName} {year}
          </span>
          <button
            onClick={() => changeMonth(1)}
            className="p-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white border border-slate-700"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-2 mb-2 text-center text-xs font-semibold text-slate-400">
        <div>Sun</div>
        <div>Mon</div>
        <div>Tue</div>
        <div>Wed</div>
        <div>Thu</div>
        <div>Fri</div>
        <div>Sat</div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {/* Empty padding slots before 1st day */}
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} className="min-h-[70px] bg-slate-950/20 rounded-lg border border-slate-900/50" />
        ))}

        {/* Days of the month */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1;
          const formattedDay = String(dayNum).padStart(2, '0');
          const formattedMonth = String(month + 1).padStart(2, '0');
          const dateKey = `${year}-${formattedMonth}-${formattedDay}`;
          const dayData = dataMap[dateKey];

          let bgClass = 'bg-slate-950/50 border-slate-800/80 hover:border-slate-700';
          let textColor = 'text-slate-400';

          if (dayData && dayData.total_trades > 0) {
            if (dayData.net_r > 0) {
              bgClass = 'bg-emerald-950/40 border-emerald-500/50 hover:border-emerald-400 shadow-sm shadow-emerald-950/30';
              textColor = 'text-emerald-300';
            } else if (dayData.net_r < 0) {
              bgClass = 'bg-rose-950/40 border-rose-500/50 hover:border-rose-400 shadow-sm shadow-rose-950/30';
              textColor = 'text-rose-300';
            } else {
              bgClass = 'bg-blue-950/40 border-blue-500/50 hover:border-blue-400';
              textColor = 'text-blue-300';
            }
          }

          return (
            <div
              key={dateKey}
              onClick={() => dayData && setSelectedDay(dayData)}
              className={`min-h-[70px] p-2 rounded-lg border transition-all cursor-pointer flex flex-col justify-between ${bgClass}`}
            >
              <div className="flex justify-between items-start">
                <span className="text-xs font-bold text-slate-300 font-mono">{dayNum}</span>
                {dayData && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-900/80 text-cyan-400 font-mono font-bold">
                    {dayData.total_trades}T
                  </span>
                )}
              </div>

              {dayData ? (
                <div>
                  <p className={`text-xs font-bold font-mono ${textColor}`}>
                    {dayData.net_r >= 0 ? `+${dayData.net_r}` : dayData.net_r}R
                  </p>
                  <p className="text-[10px] text-slate-400">
                    Disc: <span className="text-yellow-400 font-semibold">{dayData.discipline_score}%</span>
                  </p>
                </div>
              ) : (
                <span className="text-[10px] text-slate-600 block text-right">—</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected Day Details Modal */}
      {selectedDay && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-5 shadow-2xl">
            <div className="flex justify-between items-center mb-4 border-b border-slate-800 pb-3">
              <div>
                <h4 className="text-base font-bold text-white font-mono">{selectedDay.date} Summary</h4>
                <p className="text-xs text-slate-400">{selectedDay.total_trades} trades executed</p>
              </div>
              <button
                onClick={() => setSelectedDay(null)}
                className="px-2 py-1 bg-slate-800 text-slate-400 hover:text-white rounded text-xs"
              >
                Close
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center mb-4">
              <div className="p-2 bg-slate-950 rounded border border-slate-800">
                <span className="text-[10px] text-slate-400 block uppercase">Net Result</span>
                <span className={`text-sm font-bold font-mono ${selectedDay.net_r >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {selectedDay.net_r >= 0 ? `+${selectedDay.net_r}` : selectedDay.net_r}R
                </span>
              </div>
              <div className="p-2 bg-slate-950 rounded border border-slate-800">
                <span className="text-[10px] text-slate-400 block uppercase">Wins / Losses</span>
                <span className="text-sm font-bold font-mono text-white">
                  {selectedDay.wins}W / {selectedDay.losses}L
                </span>
              </div>
              <div className="p-2 bg-slate-950 rounded border border-slate-800">
                <span className="text-[10px] text-slate-400 block uppercase">Discipline</span>
                <span className="text-sm font-bold font-mono text-yellow-400">
                  {selectedDay.discipline_score}%
                </span>
              </div>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {selectedDay.trades.map((t) => (
                <div
                  key={t.id}
                  className="p-2.5 bg-slate-950/80 rounded border border-slate-800 flex items-center justify-between text-xs"
                >
                  <div className="flex items-center gap-2">
                    {t.outcome === 'win' ? (
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-rose-400" />
                    )}
                    <div>
                      <span className="font-bold text-white uppercase">{t.instrument}</span>
                      <span className="text-slate-500 ml-1 font-mono">({t.direction})</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {t.icc_score && (
                      <span className="px-1.5 py-0.5 rounded bg-cyan-950 text-cyan-400 font-mono text-[10px] font-bold border border-cyan-800">
                        {t.icc_score}
                      </span>
                    )}
                    <span className={`font-mono font-bold ${t.outcome === 'win' ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {t.outcome === 'win' ? `+${t.rr_ratio}R` : '-1R'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
